import React from "react";
import { DateTime } from "luxon";
import { ChangeService, TariffHistory } from "@/@types/changeservice";
import { getSingleChangeService, updateChangeService } from "@/api/changeservice";
import { genericGtmEvent } from "@/tracking/tracking/GTM";
import { getLocalStorageValue } from "@/api/localStorage";
import i18n from "@/i18n";
import { ChangeServiceStatus } from "@/changeservice/misc/changeServiceStatus";
import { PopupContent, RejectionReason } from "./types";

export interface PartialDateParts {
  day?: number;
  month?: number;
  year?: number;
}

export const navigateTo = (path: string) => {
  const routeEvent = new CustomEvent("RouteEvent", { detail: { path } });
  window.dispatchEvent(routeEvent);
};

export const createInlineAddress = (street?: string, streetNumber?: string, postalcode?: string, location?: string) => {
  // just return address information when given
  if (street && streetNumber && postalcode && location) {
    return `${street} ${streetNumber}, ${postalcode} ${location}`;
  }
  // Workaround to return an empty string which is not removed from the list header component. If you return a normal empty string the component will not render.
  return String.fromCharCode(160);
};

const sortTariffs = (a: TariffHistory, b: TariffHistory) => {
  const aDate = a.contractStartDate ? DateTime.fromISO(a.contractStartDate) : 0;
  const bDate = b.contractStartDate ? DateTime.fromISO(b.contractStartDate) : 0;

  if (aDate > bDate) {
    return -1;
  }
  if (aDate < bDate) {
    return 1;
  }

  return 0;
};

export const filterTariffs = (tariffs?: TariffHistory[]) => {
  const _tariffs = tariffs?.filter((tariff) => tariff.isCurrent || tariff.isUpcoming || tariff.isPast);
  const upcomingTariff = _tariffs?.find((tariff) => tariff.isUpcoming);
  const currentTariff = _tariffs?.find((tariff) => tariff.isCurrent);
  const pastTariffs = _tariffs?.filter((tariff) => tariff.isPast).sort(sortTariffs);

  return {
    upcomingTariff,
    currentTariff,
    pastTariffs,
  };
};

export const getI18nStatusTranslation = (status?: string, i18key?: string) => {
  return {
    headline: [i18key, status, "headline"].join("."),
    subText: [i18key, status, "subText"].join("."),
    ctaButton: [i18key, status, "ctaButton"].join("."),
  };
};

export const addDaysToDate = (dateStr: string, daysToAdd: number) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split("T")[0];
};

export const ctaButtonMailTypes = ["CHANGE_CANCELLED", ChangeServiceStatus.DEACTIVATED];

export const frontendDateFormat = "dd.MM.yyyy";
export const backendDateFormat = "yyyy-MM-dd";
export const backendDatetimeFormat = "yyyy-MM-ddTHH:mm:ss";

export const formatDate = (
  date?: Date | string | null,
  dateFormat: string = frontendDateFormat,
  parseFormat: string = backendDateFormat
) => {
  if (date) {
    if (typeof date === "string") {
      return DateTime.fromFormat(date, parseFormat).toFormat(dateFormat);
    }

    return DateTime.fromJSDate(date).toFormat(dateFormat);
  }
};

export const toLocalNumber = (number?: number | string, withDecimal = true) => {
  return number
    ? String(
        Number(number).toLocaleString("de-DE", { minimumFractionDigits: withDecimal ? 2 : 0, maximumFractionDigits: 2 })
      )
    : "";
};

// eslint-disable-next-line
export const isPresent = (value?: any) => {
  return (
    typeof value !== "undefined" &&
    value !== undefined &&
    value != null &&
    value !== "" &&
    (typeof value !== "number" || isFinite(value))
  );
};

export const fromDateParts = ({ day = 0, month = 0, year = 0 }: PartialDateParts = {}) => {
  const date = DateTime.fromObject({ day, month, year });

  if (date.isValid) {
    return date.toJSDate();
  }
};

export const toDate = (value: string | PartialDateParts) => {
  if (typeof value === "string") {
    const date = DateTime.fromFormat(value, backendDateFormat);
    if (date.isValid) {
      return date.toJSDate();
    }
  } else {
    return fromDateParts(value);
  }
};

export const isFutureDate = (value: string | PartialDateParts) => {
  const date = toDate(value);
  if (date && isPresent(date)) {
    return date.getTime() >= Date.now();
  }
  return false;
};

export function formatCurrency(number?: number) {
  if (number !== undefined) {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(number);
  }
}

export const unitString = (amount?: number | string, unit = "month") => {
  if (!amount) return "";
  const finalUnit = String(unit) + (amount === 0 || (amount as number) > 1 ? "s" : "");
  return amount + " " + i18n.t(finalUnit);
};

export const formatName = (salutation?: string, title?: string, firstname?: string, lastname?: string) => {
  const result = [];
  if (salutation) {
    result.push(salutation);
  }
  if (title) {
    result.push(title);
  }
  if (firstname) {
    result.push(firstname);
  }
  if (lastname) {
    result.push(lastname);
  }
  return result.join(" ");
};

type toggleIsActiveNoCategoryType = {
  changeService: ChangeService;
  userId: string;
  deactivationValues?: deactivationValuesT;
};

type deactivationValuesT = {
  changeReason: string;
  changeReasonText: string;
};

export const toggleIsActiveNoCategory = async ({
  changeService,
  userId,
  deactivationValues,
}: toggleIsActiveNoCategoryType): Promise<void> => {
  const singleCS = await getSingleChangeService(userId, changeService.id);
  const patch = {
    ...singleCS,
    version: singleCS?.version,
    active: !singleCS?.active,
    deactivationValues: deactivationValues,
  };

  // // Send tracking
  // const rmd_trx = LocalStorage.get("rmd_trx");
  // rmdTrx(!changeService.active ? "CS_ACTIVATED" : "CS_DEACTIVATED", userId, changeService.id, rmd_trx);

  const rmd_trx = getLocalStorageValue("rmd_trx");
  const payload = {
    event: !changeService?.active ? "CS_ACTIVATED" : "CS_DEACTIVATED",
    user_id: userId,
    ref_id: changeService.id,
    rmd_trx,
  };

  genericGtmEvent("RMD_TRX", payload);

  const updatedCS = await updateChangeService(userId, changeService.id, patch);
  return updatedCS;
};

export const scrollToElement = (el: HTMLElement, behavior: ScrollBehavior = "smooth", yOffset = 0) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const mainLayout: HTMLElement = document.getElementById("mainLayout")!;
  const top = el.getBoundingClientRect().top + mainLayout.scrollTop + yOffset;

  if (mainLayout) {
    try {
      mainLayout.scroll({ left: 0, top, behavior });
    } catch (err) {
      mainLayout.scrollTop = top;
      mainLayout.scrollLeft = 0;
    }
  }
};

export const removeEmptyProperties = <T extends object>(object: T): Partial<T> => {
  const result: Partial<T> = {};
  Object.entries(object).forEach(([key, value]) => {
    if (value) {
      result[key as keyof T] = value;
    }
  });
  return result;
};

export function formatForBackend(date?: Date | string, parseFormat: string = frontendDateFormat) {
  if (!date || (date instanceof Date && date.getFullYear() === 0)) {
    return "";
  }

  let parsedDate;
  if (typeof date === "string") {
    parsedDate = DateTime.fromFormat(date, parseFormat);
  } else {
    parsedDate = DateTime.fromJSDate(date);
  }

  if (!parsedDate.isValid) {
    // Convert Date object to a string in the backend format if date is not a string
    const dateAsString = typeof date === "string" ? date : parsedDate.toFormat(backendDateFormat);
    parsedDate = DateTime.fromFormat(dateAsString, backendDateFormat);

    if (!parsedDate.isValid) {
      throw Error(
        `Invalid date format for ${date && date.toString()}. Expected format ${parseFormat} or ${backendDateFormat}`
      );
    }
  }

  return parsedDate.toFormat(backendDateFormat);
}

export const ALLOWED_CHARACTERS = "[\\w \\-,.|/]*";
export const AT_LEAST_1_DIGIT = new RegExp("^(?=(?:.*?\\d){1})" + ALLOWED_CHARACTERS + "$");
export const AT_LEAST_4_DIGITS = new RegExp("^(?=(?:.*?\\d){4})" + ALLOWED_CHARACTERS + "$");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isMaxLength = (maxLength: number) => (value: string | any[]) => {
  return value && value.length <= maxLength;
};

export const validateCustomerNumber =
  (counterNumber: string | null | undefined) => (customerNumber: string | null | undefined) => {
    if (!!counterNumber && !customerNumber) return i18n.t("validation:isCustomerNumberMissing");
    if (customerNumber && !AT_LEAST_4_DIGITS.test(customerNumber)) return i18n.t("validation:validCustomerNumber");
    return customerNumber && !isMaxLength(30)(customerNumber) ? undefined : i18n.t("validation:maxLengthExceeded");
  };

export const validateCounterNumber =
  (customerNumber: string | null | undefined) => (counterNumber: string | null | undefined) => {
    if (!counterNumber && !customerNumber) return undefined;
    if (customerNumber && !counterNumber) return i18n.t("validation:isCounterNumberMissing");
    if (counterNumber === customerNumber) return i18n.t("validation:notSameCounterCustomerNumber");
    if (counterNumber && !AT_LEAST_4_DIGITS.test(counterNumber)) return i18n.t("validation:validCounterNumber");
    return counterNumber && isMaxLength(30)(counterNumber);
  };

export const wait = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getRejectionPopupContent = (reason: RejectionReason): PopupContent => {
  switch (reason) {
    case "Mitarbeiter - Sterbefall":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Es gab einen Trauerfall",
        content: (
          <p>
            Wir haben einen Trauerfall vermerkt. Aus rechtlichen Gründen ist eine Aktivierung des Wechselservices nicht
            möglich. Bitte erstellen Sie einen neuen Wechselauftrag.
          </p>
        ),
        cta: "Jetzt Wechselservice erstellen",
        link: `/wechselservice/strom/abschließen`,
      };
    case "Mitarbeiter - Umzug":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Sie sind umgezogen",
        content: (
          <p>
            Wir haben Ihren Umzug registriert. Um sicherzustellen, dass Ihre Wechsel weiterhin problemlos funktionieren,
            bitten wir Sie, einen neuen Wechselservice anzulegen.
          </p>
        ),
        cta: "Jetzt Wechselservice erstellen",
        link: `/wechselservice/strom/abschließen`,
      };

    case "Mitarbeiter - Ablehnung anderer Versorger":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Ablehnung durch anderen Versorger",
        content: (
          <p>
            Der Anbieter, zu dem wir Sie wechseln möchten, hat uns mitgeteilt, dass Sie bereits einen laufenden Vertrag
            bei einem anderen Anbieter haben. Bitte erstellen Sie einen neuen Wechselauftrag.
          </p>
        ),
        cta: "Jetzt Wechselservice erstellen",
        link: `/wechselservice/strom/abschließen`,
      };

    case "Mitarbeiter - Ablehnung Bonitaet":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Ablehnung wegen Bonität",
        content: (
          <p>
            Leider können wir aufgrund fehlender Bonität keinen Anbieterwechsel für Sie vornehmen. Wir informieren Sie
            per E-Mail, sobald wir einen passenden Anbieter in unser Portfolio aufnehmen.
          </p>
        ),
        cta: "",
      };

    case "Mitarbeiter - Waermepumpe/ HT/NT/ Nachtspeicher":
      return {
        title: "Wechselservice derzeit nicht verfügbar",
        subtitle: "Wechselservice nicht verfügbar",
        content: (
          <>
            <p>
              Wir können momentan für folgende Zähler- oder Energiearten keinen Wechselservice realisieren.{" "}
              <p>Dies betrifft:</p>
            </p>
            <ul>
              <li>Nachtspeicherheizungen</li>
              <li>HT/NT (Zwei-Tarif)-Zähler</li>
              <li>Wärmepumpen mit eigenem Zähler oder HT/NT (Zwei-Tarif)-Zähler</li>
              <li>Fernwärme</li>
              <li>Flüssiggas</li>
            </ul>
            <p>
              Wir bedauern, dass wir Ihnen in diesen Fällen keinen Wechselservice anbieten können. Unser Kundenservice
              steht Ihnen jedoch gerne zur Verfügung, um alternative Lösungen zu besprechen.
            </p>
          </>
        ),
        cta: "",
      };

    case "Mitarbeiter - lt. Emarsys E-Mails nicht zustellbar":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "E-Mails nicht zustellbar",
        content: (
          <>
            <p>
              Wir können Ihnen keine E-Mails senden. Daher ist eine Reaktivierung des Wechselservices nicht möglich, da
              Sie sonst wichtige Informationen verpassen könnten.
            </p>
            <p>Nächste Schritte:</p>
            <ol>
              <li>Überprüfen Sie Ihre Email-Adresse auf Richtigkeit. Diese finden Sie unter “Mein Profil → Profil.</li>
              <li>Prüfen Sie, ob Ihr Postfach voll ist.</li>
              <li>Hat sich Ihre E-Mail geändert? Kontaktieren Sie bitte unseren Kundenservice.</li>
            </ol>
            <strong>Alternativ: </strong>Schreiben Sie uns eine Nachricht.
          </>
        ),
        cta: "Kundenservice kontaktieren",
        link: `/kontakt`,
      };

    case "Rueckmeldung - Zaehlerausbau/ Zaehlerstilllegung":
    case "Kundenwunsch - Zaehlerausbau/ Zaehlerstilllegung":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Zähler existiert nicht mehr",
        content: (
          <p>
            Uns wurde mitgeteilt, dass Ihr Zähler ausgebaut oder stillgelegt wurde. Da dieser Zähler nicht mehr
            existiert, können wir den Wechselservice nicht fortsetzen.{" "}
            <strong>Bitte kontaktieren Sie unseren Kundenservice.</strong>
          </p>
        ),
        cta: "Kundenservice kontaktieren",
        link: `/kontakt`,
      };

    case "Fehlende Daten":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Wichtige Informationen fehlen",
        content: (
          <p>
            Um Ihren Wechselservice abzuschließen, benötigen wir noch wichtige Daten von Ihnen. Ohne diese Informationen
            ist ein Wechsel zu einem besseren Anbieter nicht möglich. Bitte ergänzen Sie die fehlenden Daten, da
            unvollständige Anfragen abgelehnt werden.
          </p>
        ),
        cta: "Jetzt fehlende Daten nachtragen",
        link: "missingDataForm",
      };
    case "Fehlerhafte Daten":
      return {
        title: "Wechselservice-Aktivierung nicht möglich",
        subtitle: "Wichtige Informationen fehlen",
        content: (
          <p>
            Um Ihren Wechselservice abzuschließen, benötigen wir noch wichtige Daten von Ihnen. Ohne diese Informationen
            ist ein Wechsel zu einem besseren Anbieter nicht möglich. Bitte ergänzen Sie die fehlenden Daten, da
            unvollständige Anfragen abgelehnt werden.
          </p>
        ),
        cta: "Jetzt Daten nachtragen",
        link: "incorrectDataForm",
      };
  }
};

export const legalForms = [
  "AdöR",
  "AG",
  "AG & Co. KG",
  "AG & Co. OHG",
  "BGB",
  "e.G.",
  "e.K.",
  "e.V.",
  "Einzeluntern.",
  "EWIV",
  "G-REIT",
  "GbR",
  "gGmbH",
  "GmbH",
  "GmbH & Co. KG",
  "GmbH & Co. OHG",
  "HGB",
  "invAG",
  "KdöR",
  "KG",
  "KG & Co. KG",
  "KGaA",
  "Ltd.",
  "Ltd. & Co. KG",
  "OHG",
  "OHG & Co. KG",
  "PartG",
  "SCE",
  "SE",
  "Stiftung & Co. KG",
  "UG",
  "UG & Co. KG",
  "VVaG",
];
